import './OrganizationSwitcher.scss';

import { CloudOutlined } from '@ant-design/icons';
import { MenuInfo } from 'rc-menu/es/interface';
import { OrganizationMetadata } from '@sonar-software/sonar-core';
import { SalesforceCloudOutline } from '@ui/lottie';
import { When } from 'react-if';

import {
	BASE_ROUTES,
	COPY_SPECIFIC,
	FULL_ROUTES,
	ORGANIZATION_SWITCHER_COPY
} from '@sonar-software/sonar-ts-constants';
import { Button, Space, Tooltip } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { SonarMenu, SonarMenuItem } from '@ui/menu';
import {
	useCurrentOrganization,
	useSalesforceOrganizations,
	useSwitchOrganization
} from '@hooks';
import { useLocation, useNavigate } from 'react-router-dom';

function displayName(org: OrganizationMetadata): string {
	return org.isSandbox ? `${org.name} (sandbox)` : org.name;
}

function buildMenuItems(
	organizations: Array<OrganizationMetadata>,
	isClosed: boolean,
	title: string,
	selectedKey: number
): Array<SonarMenuItem> {
	const subMenu: SonarMenuItem = {
		children: [],
		key: 'org-submenu',
		label:
	<>
		<CloudOutlined />
		<When condition={ !isClosed }>
			<Tooltip
				mouseEnterDelay={ 0.5 }
				placement='bottom'
				title={ title }>
				{ title }
			</Tooltip>
		</When>
	</>

	};

	for (const org of organizations) {
		const isSelected = org.id === selectedKey;
		const orgDisplayName = displayName(org);
		const showIcon = org.salesforceSyncInProgress && !org.hasSalesforceData;

		subMenu.children?.push({
			className: isSelected ? 'selected-org' : '',
			id: `switch-org-${org.id}`,
			key: String(org.id),
			label:
	<Tooltip
		mouseEnterDelay={ 0.5 }
		placement='right'
		title={ orgDisplayName }>
		<Space className='org-entry-wrapper'>
			<span className='switcher-org-name'>{ orgDisplayName }</span>
			{ showIcon && <SalesforceCloudOutline className='syncing-icon' /> }
		</Space>
	</Tooltip>
		});
	}

	subMenu.children?.push({
		id: 'update-organizations',
		key: FULL_ROUTES.SETTINGS_ORGANIZATION,
		label:
	<Button
		ghost
		id='update-organizations-button'>
		{ COPY_SPECIFIC.CONFIGURE_ORGANIZATIONS }
	</Button>

	});

	return [ subMenu ];
}

export interface OrganizationSwitcherProps {
	isClosed: boolean;
}

export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ isClosed }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const organizations = useSalesforceOrganizations();
	const currentOrganization = useCurrentOrganization();

	const requestSwitch = useSwitchOrganization();

	const selectedKey = currentOrganization?.id ?? -1;
	const title =
		currentOrganization && displayName(currentOrganization) ?
			displayName(currentOrganization) :
			ORGANIZATION_SWITCHER_COPY.PLACEHOLDER;

	const handleClick = useCallback(
		(info: MenuInfo) => {
			if (info.key.startsWith('/')) {
				navigate(info.key);

				return;
			}

			const organizationId = Number(info.key);

			if (!isNaN(organizationId) && organizationId !== selectedKey) {
				requestSwitch({ organizationId }, () => {
					const { pathname } = location;

					if (pathname.includes(BASE_ROUTES.PERISCOPE)) {
						navigate(FULL_ROUTES.DASHBOARD);
					} else if (pathname.includes(`${BASE_ROUTES.DATA_DICTIONARY}`)) {
						navigate(FULL_ROUTES.DATA_DICTIONARY);
					}
				});
			}
		},
		[ location, navigate, requestSwitch, selectedKey ]
	);

	const menuItems = useMemo(
		() => buildMenuItems(organizations, isClosed, title, selectedKey),
		[ isClosed, organizations, selectedKey, title ]
	);

	return (
		<SonarMenu
			className='org-switcher-menu'
			id='org-switcher-menu'
			inlineIndent={ 8 }
			items={ menuItems }
			mode='inline'
			onClick={ handleClick }
			selectedKeys={ [ '-1' ] }
			theme='dark' />
	);
};
